import { accountingService } from '@/services/accounting-service';
import { dataService } from '@/services/data-service';

/**
 * Shortcode pattern: digits followed by exactly 2 uppercase letters
 * Examples: "123AB", "456CD", "1XY"
 */
const SHORTCODE_PATTERN = '(\\d+)([A-Z]{2,3})';

/**
 * Cache for shortcode lookups to avoid repeated API calls
 */
const shortcodeCache = new Map<string, Map<string, string>>();

export interface ShortcodeAccountInfo {
	accountId: string;
	accountNumber?: string;
	ownerLabel?: string;
	address?: string;
}

/**
 * Cache of shortcode → account info per association. Built lazily by scanning
 * each account's freeform `address` for shortcode tokens, joined with attached
 * people for owner labels. Negative results are stored as an empty Map so we
 * don't refetch on every render.
 */
const accountByShortcodeCache = new Map<string, Map<string, ShortcodeAccountInfo>>();


/**
 * Get all shortcodes for a specific association
 * @param associationId - The association ID
 * @returns The shortcodes for the association
 */
export async function getShortcodesForAssociation(associationId: string): Promise<Map<string, string>> {
	const associationCache = shortcodeCache.get(associationId);
	if (associationCache) {
		return associationCache;
	}
	const response = await dataService.getAssociationShortcutCodes({
		associationId,
	});
	if (response.data) {
		const cache = new Map(response.data.map((code) => [
			code.shortcutCode, `${code.fullAddress || ''}, ${code.city || ''}, ${code.state || 'OH'} ${code.zipCode || ''}`,
		]));
		shortcodeCache.set(associationId, cache);
		return cache;
	}
	return new Map();
}

/**
 * Look up a shortcode for a specific association
 * @param associationId - The association ID
 * @param shortcode - The shortcode to look up (e.g., "123AB")
 * @returns The full address or null if not found
 */
export async function lookupShortcode(associationId: string, shortcode: string): Promise<string | null> {
	try {
		const shortcodesForAssociation = await getShortcodesForAssociation(associationId);
		return shortcodesForAssociation.get(shortcode) || null;
	} catch (error) {
		console.error('Error looking up shortcode:', error);
		return null;
	}
}

/**
 * Replace all shortcode patterns in text with full addresses
 * @param text - The text containing shortcodes
 * @param associationId - The association ID for context
 * @returns Promise resolving to text with shortcodes replaced
 */
export async function replaceShortcodes(text: string, associationId: string): Promise<string> {
	if (!text || !associationId) {
		console.log('No text or associationId', { text, associationId });
		return text;
	}
	const shortcodesForAssociation = await getShortcodesForAssociation(associationId);

	// Find all shortcode patterns in the text
	return text.replaceAll(new RegExp(SHORTCODE_PATTERN, 'g'), (match, addrPart, shortcode) => {
		const shortcodePart = shortcodesForAssociation.get(shortcode);
		if (shortcodePart) {
			return `${addrPart} ${shortcodePart}`;
		}
		return match;
	});
}

/**
 * Extract the first shortcode token from a freeform string, e.g. "4769SA"
 * from "4769 St Andrews Drive, Grove City, OH". Returns null if none found.
 */
export function extractShortcode(text: string | null | undefined): string | null {
	if (!text) return null;
	const m = text.match(new RegExp(SHORTCODE_PATTERN));
	return m ? `${m[1]}${m[2]}` : null;
}

/** Strip non-alphanumerics and lowercase for fuzzy address comparisons. */
function normalizeAddress(s: string): string {
	return s.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Street address → the association's own unit shortcode, from the map
 * `getShortcodesForAssociation` returns. Used when an importer has an address
 * and needs the unit number that goes with it.
 *
 * ## The shortcut-code table is the only authority — never derive one
 *
 * The codes look derivable: "10213 Holmhill Ln" → 10213 + H + L. But it is not
 * one rule. "7487 Hazy Gate Ln" is 7487HG, taking both initials from the street
 * NAME, where Holmhill takes one from the name and one from the type. A guess
 * produces a plausible-looking number that collides with, or silently shadows,
 * a real unit — and an account number is identity here. No shortcut code means
 * no answer, and the caller asks a human.
 *
 * PREFIX match, because the shortcut table stores a full postal address
 * ("10213 Holmhill Ln, Dublin, OH 43017") while a utility register prints only
 * the street line.
 */
export function shortcodeForAddress(codes: Map<string, string>, address: string): string | null {
	const target = normalizeAddress(address);
	// A 3-character target ("12 A") would prefix-match half the portfolio.
	if (target.length < 4) return null;
	for (const [code, fullAddress] of codes) {
		if (normalizeAddress(fullAddress).startsWith(target)) return code;
	}
	return null;
}

/**
 * Build (and cache) a map of shortcode → account info for an association by
 * scanning every account's freeform address, accountNumber, and falling back
 * to a normalized match against the association's shortcut-code expansions.
 * Joined with attached people for an owner label. Returns an empty map if the
 * caller lacks permission.
 */
async function getAccountsByShortcodeForAssociation(associationId: string): Promise<Map<string, ShortcodeAccountInfo>> {
	const cached = accountByShortcodeCache.get(associationId);
	if (cached) return cached;
	const map = new Map<string, ShortcodeAccountInfo>();
	try {
		const [accountsResp, peopleResp, shortcuts] = await Promise.all([
			accountingService.listAccounts({ associationId, limit: 1000 }),
			accountingService.listPeople({ associationId, limit: 1000 }).catch(() => ({ data: [] })),
			getShortcodesForAssociation(associationId).catch(() => new Map<string, string>()),
		]);
		const peopleByAccount = new Map<string, string[]>();
		for (const p of peopleResp.data ?? []) {
			if (!p.accountId) continue;
			const name = [p.firstName, p.lastName].filter(Boolean).join(' ').trim();
			if (!name) continue;
			const list = peopleByAccount.get(p.accountId) ?? [];
			list.push(name);
			peopleByAccount.set(p.accountId, list);
		}
		const shortcutByNormAddress = new Map<string, string>();
		for (const [code, fullAddress] of shortcuts.entries()) {
			shortcutByNormAddress.set(normalizeAddress(fullAddress), code);
		}
		const re = new RegExp(SHORTCODE_PATTERN, 'g');
		const upsert = (shortcode: string, acct: { id: string; accountNumber?: string; address?: string }) => {
			if (map.has(shortcode)) return;
			const owners = peopleByAccount.get(acct.id) ?? [];
			map.set(shortcode, {
				accountId: acct.id,
				accountNumber: acct.accountNumber,
				ownerLabel: owners.length > 0 ? owners.join(' & ') : undefined,
				address: acct.address,
			});
		};
		for (const acct of accountsResp.data ?? []) {
			// 1) Shortcode tokens embedded in the account's freeform address.
			if (acct.address) {
				for (const m of acct.address.matchAll(re)) upsert(`${m[1]}${m[2]}`, acct);
			}
			// 2) Account number itself shaped like a shortcode.
			if (acct.accountNumber) {
				const numMatch = acct.accountNumber.match(new RegExp(`^${SHORTCODE_PATTERN}$`));
				if (numMatch) upsert(`${numMatch[1]}${numMatch[2]}`, acct);
			}
			// 3) Fuzzy: account.address normalized matches a shortcut-code expansion.
			if (acct.address) {
				const code = shortcutByNormAddress.get(normalizeAddress(acct.address));
				if (code) upsert(code, acct);
			}
		}
	} catch {
		// User likely lacks accounting permission; cache empty so we don't refetch.
	}
	accountByShortcodeCache.set(associationId, map);
	return map;
}

/**
 * Resolve the first shortcode in `text` to an account in this association,
 * if one exists. Returns null when there's no shortcode, no match, or the
 * caller lacks accounting access.
 */
export async function findAccountForShortcode(
	text: string | null | undefined,
	associationId: string | null | undefined,
): Promise<({ shortcode: string } & ShortcodeAccountInfo) | null> {
	if (!text || !associationId) return null;
	const shortcode = extractShortcode(text);
	if (!shortcode) return null;
	const map = await getAccountsByShortcodeForAssociation(associationId);
	const info = map.get(shortcode);
	return info ? { shortcode, ...info } : null;
}

/**
 * Clear the shortcode cache for a specific association or all associations
 * @param associationId - Optional association ID to clear cache for
 */
export function clearShortcodeCache(associationId?: string): void {
	if (associationId) {
		shortcodeCache.delete(associationId);
		accountByShortcodeCache.delete(associationId);
	} else {
		shortcodeCache.clear();
		accountByShortcodeCache.clear();
	}
}
